import { Injectable } from '@angular/core';
import { ApplicationSettingsType, FileType } from '@fi-shared/types';
import { IUpgradeLogEntry, ISiteDesign } from '@fi-shared/models';
import { ReplaySubject, Subject } from 'rxjs';
import { rc4Decrypt } from '@fi-shared/utils/rc4-decrypt';
import { IContentLanguage } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { SupportContactFrequencyType } from '@fi-shared/types/support-contact-frequency-type';

export interface IFiCustomerDataAdConfig {
    Id: number;
    Name: string;
}

export interface IFiApplicationSettingAccessType {
    ApplicationSettingType: ApplicationSettingsType;
    AccessRightTypes: Array<number>;
    IsSystemObject: boolean;
}

export interface IFiCustomerSupportContact {
    EmailAddress: string;
    PhoneNumber: string;
    FrequencyType: SupportContactFrequencyType;
    SupportManagerId: number;
    AllowSupportChat: boolean;
}

export interface IPKCustomerData {
    IsAutoImportEnabled: boolean;
    PKUnitIds: number[];
}

export interface IFiCustomerData {
    AdConfigs: IFiCustomerDataAdConfig[];
    DefaultApplicationLanguageType: number;
    CustomerName: string;
    CustomerId: number;
    CustomButtons: Array<IFiCustomerDataCustomButton>;
    ApplicationSettingAccessTypes: IFiApplicationSettingAccessType[]; // Activated modules
    ContentLanguages: IContentLanguage[];
    UpcomingVersion: IUpgradeLogEntry;
    SiteDesign: ISiteDesign;
    ChecklistPresenceEnabled: boolean;
    StorageDomain: string;
    ShutOffDate: string; // ISO 8601 datetime

    SupportContact: IFiCustomerSupportContact;

    MobileAppName: string;
    DisplayName: string;
    SessionExpire: number;
    UserIPAddress: string;
    CanShareAsNews: boolean;
    NewsFeedSummaryLength: number;
    ApplicationLanguageAccess: number[];
    ShowStayLoggedIn: boolean;
    GAAccount: string;
    Senders: string[];
    UserApplicationLanguageType: number; // from accept-language request header
    IsRequestAccessAvailable: boolean;
    MobileAppUsage: boolean;
    UseDefaultContentLanguage: boolean;
    IsMobileAppWebMailEnabled: boolean;
    FileTypeExtensions: {
        [prop: string]: FileType;
    };
    FileUploadLimit: number;
    MiddleFilesUploadLimit: number;
    LargeFilesUploadLimit: number;
    MiddleFiles: string[];
    LargeFiles: string[];
    VimeoEnabled: boolean;
    VimeoToken: string;
    HasTemplateCustomer: boolean;
    PrivateObjectStorageUsage: boolean;
    AllowedVimeoDomains: string[];
    UserAgentInfo: {
        ClientType: string;
        ClientName: string;
        ClientVersion: string;
        OsName: string;
        OsVersion: string;
    };
    PersonalkollenData: IPKCustomerData;
}

export interface IFiCustomerDataCustomButton {
    Captions: Array<{ LanguageId: number; Text: string }>;
    IconClass: string;
    Id: number;
    Names: Array<any>;
    SortIndex: number;
    Url: string;
    OpenInSameWindow: boolean;
}

@Injectable()
export class FiCustomerDataService {
    private _customerData: IFiCustomerData;

    onCustomerDataChange$ = new Subject<IFiCustomerData>();

    constructor() {}

    getCustomerData(): IFiCustomerData {
        return this._customerData;
    }

    get customerData(): IFiCustomerData {
        return this.getCustomerData();
    }

    getBrandingColor(): string {
        return this.customerData.SiteDesign.BrandingColor;
    }

    private _vimeoToken$ = new ReplaySubject<string>(1);
    get vimeoToken$() {
        return this._vimeoToken$.asObservable();
    }
    setCustomerData(customerData: IFiCustomerData) {
        if (customerData) {
            const decodedVimeoToken = rc4Decrypt(customerData.CustomerName, customerData.VimeoToken);
            this._vimeoToken$.next(decodedVimeoToken);
        }

        if (this._customerData === customerData) return;

        if (this._customerData && customerData && this._customerData.CustomerId === customerData.CustomerId) return;

        this._customerData = customerData;
        this.onCustomerDataChange$.next(this._customerData);
    }

    clearCustomerData() {
        this._customerData = undefined;
        this.onCustomerDataChange$.next(this._customerData);
    }

    isADLogin() {
        return this.customerData.AdConfigs && this.customerData.AdConfigs.length > 0;
    }

    isTestCustomer() {
        const customerData = this.getCustomerData();
        return (
            !customerData ||
            customerData.CustomerName === 'DevRollout' ||
            customerData.CustomerName === 'DevStaging' ||
            customerData.CustomerName === 'StagingAD' ||
            customerData.CustomerName === 'Stabletesting' ||
            customerData.CustomerName === 'StatesideBurgersUS'
        );
    }

    /*isAIAssistanceDevCustomer() {
        const allowedCustomerNames = ['DevStaging', 'DevRollout', 'Templates'];
        const customerData = this.getCustomerData();
        return !customerData || allowedCustomerNames.includes(customerData.CustomerName);
    }*/

    isChatSupported() {
        const customerData = this.getCustomerData();
        if (!customerData) return true;
        if (customerData.UserAgentInfo.OsName === 'iOS') {
            const versionParts = (customerData.UserAgentInfo.OsVersion ?? '').split('.');
            const isOldSafari = versionParts.length >= 2 && versionParts.length <= 3 && parseInt(versionParts[0]) <= 12;
            return !isOldSafari;
        }
        return true;
    }

    privateObjectStorageUsage(): boolean {
        return this.getCustomerData()?.PrivateObjectStorageUsage;
    }

    get checklistAttachmentOldTimeIntervalMs() {
        // images older than 6 hrs can't be attached to checkpoint answer
        return 1000 * 60 * 60 * 6;
    }

    getSupportPhoneNumber() {
        return this.customerData.SupportContact?.PhoneNumber ?? '';
    }

    getSupportEmailAddress() {
        return this.customerData.SupportContact?.EmailAddress ?? '';
    }

    setPersonalkollenData(personalkollenData: IPKCustomerData) {
        this.customerData.PersonalkollenData = personalkollenData;
    }

    isStaging(): boolean {
        const customerData = this.getCustomerData();
        return customerData.CustomerName === 'DevStaging';
    }

    isSynchronizedOnboardingEnabled(): boolean {
        // note: hiding this feature for now (until 7.00 release)
        return false;

        const customerData = this.getCustomerData();
        return customerData.CustomerName === 'DevStaging';
    }
}
